// Busca da Conferência do Faturamento (Projeto 12, tarefa 5836). Saiu de
// views/Financeiro/ConferenciaFaturamentoView.tsx para a tela inicial da Gestão do
// Financeiro (#6499) usar a mesma conta: o número da tela inicial é o da Conferência.
//
// A regra (o que é faturado, o prazo, o limite) está em conferenciaFaturamento.ts.
import { supabase } from './supabase';
import {
  escolherPlanilhaBase,
  mesAnterior,
  mesDeOrdem,
  mesPlanilhaDasFaturas,
  montarConferencia,
  type FaturaAnteriorConferencia,
  type FaturaConferencia,
  type ItemRateioConferencia,
  type ResultadoConferencia,
} from './conferenciaFaturamento';

const TAMANHO_PAGINA_BANCO = 1000;

const CAMPOS_FATURA = [
  'id', 'uc_codigo', 'uc_codigo_norm', 'cliente_nome', 'mes_referencia', 'classificacao', 'status_fatura',
  'leitura_anterior', 'leitura_atual', 'proxima_leitura', 'data_apresentacao', 'data_vencimento', 'created_at',
  'emitido_em', 'emitido_por_nome', 'pular_calculo_em', 'pular_calculo_por_nome', 'motivo_pular_calculo',
  'motivo_pular_calculo_codigo', 'pular_calculo_detalhe', 'num_cobranca_boleto', 'linha_digitavel_boleto', 'url_boleto',
  'empresa_parceira_id', 'status_boleto_vigor', 'cancelado_em', 'motivo_atraso_emissao',
  'justificativa_atraso_emissao', 'energia_injetada_gd1_kwh', 'saldo_ugs_vigor', 'historico_emissoes',
].join(', ');

/** Busca todas as páginas — o PostgREST corta em 1.000 linhas e a planilha de JUL/2026 já tem 1.056 linhas. */
async function buscarTudo<T>(montar: (de: number, ate: number) => PromiseLike<{ data: T[] | null; error: any }>): Promise<T[]> {
  const todas: T[] = [];
  for (let de = 0; ; de += TAMANHO_PAGINA_BANCO) {
    const { data, error } = await montar(de, de + TAMANHO_PAGINA_BANCO - 1);
    if (error) throw error;
    todas.push(...(data ?? []));
    if (!data || data.length < TAMANHO_PAGINA_BANCO) return todas;
  }
}

export const hojeIsoLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Mês corrente no formato das faturas: "OUT/2026". */
export const mesAtualFaturas = () => {
  const d = new Date();
  return mesDeOrdem(d.getFullYear() * 12 + d.getMonth());
};

export interface CargaConferencia {
  mes: string;
  /** Mês da planilha que VALE para as faturas do mês (o anterior). */
  planilhaEsperada: string | null;
  planilhaMes: string | null;
  planilhaCarregadaEm: string | null;
  resultado: ResultadoConferencia;
}

/** Monta a conferência das faturas de `mesAlvo` ("OUT/2026") contra a planilha de rateio do mês anterior. */
export async function carregarConferencia(mesAlvo: string): Promise<CargaConferencia> {
  const { data: planilhas, error: erroPlanilhas } = await supabase
    .from('rateios_credito')
    .select('id, mes_referencia, created_at')
    .order('created_at', { ascending: true });
  if (erroPlanilhas) throw erroPlanilhas;
  // Rateio de um mês gera as faturas do mês seguinte: faturas de AGO/2026 usam a planilha de JUL/2026.
  const planilhaEsperada = mesPlanilhaDasFaturas(mesAlvo);
  const planilha = planilhaEsperada ? escolherPlanilhaBase(planilhas ?? [], planilhaEsperada) : null;

  const anterior = mesAnterior(mesAlvo);
  const [itens, faturas, faturasAnteriores] = await Promise.all([
    planilha
      ? buscarTudo<ItemRateioConferencia>((de, ate) =>
          supabase.from('rateios_credito_itens')
            .select('uc, outro_numero_uc, status, nome, cpf_cnpj, uc_geradora')
            .eq('rateio_id', planilha.id).order('id').range(de, ate))
      : Promise.resolve([]),
    buscarTudo<FaturaConferencia>((de, ate) =>
      supabase.from('faturas').select(CAMPOS_FATURA)
        .eq('mes_referencia', mesAlvo).is('excluido_em', null)
        // Sem o `or`, `neq('ambiente','sandbox')` também descartaria as linhas com ambiente NULL.
        .or('ambiente.is.null,ambiente.neq.sandbox')
        .order('id').range(de, ate) as any),
    anterior
      ? buscarTudo<FaturaAnteriorConferencia>((de, ate) =>
          supabase.from('faturas').select('uc_codigo_norm, proxima_leitura')
            .eq('mes_referencia', anterior).is('excluido_em', null)
            .order('id').range(de, ate))
      : Promise.resolve([]),
  ]);

  return {
    mes: mesAlvo,
    planilhaEsperada,
    planilhaMes: planilha?.mes_referencia ?? null,
    planilhaCarregadaEm: planilha?.created_at ?? null,
    resultado: montarConferencia({ itensRateio: itens, faturas, faturasMesAnterior: faturasAnteriores, hojeIso: hojeIsoLocal() }),
  };
}
